import React, { useState, useEffect, useCallback } from 'react';
import { cpqAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Calculator, Plus, Trash2, Layers, Tag, Zap,
  Loader2, DollarSign, Percent, ChevronDown, ChevronUp, Edit
} from 'lucide-react';

export default function CPQPricingRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await cpqAPI.getRules();
      setRules(res.data.rules || []);
    } catch (e) { toast.error('Failed to load pricing rules'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try {
      await cpqAPI.deleteRule(id);
      toast.success('Rule deleted');
      load();
    } catch (e) { toast.error('Failed to delete rule'); }
  };

  const typeIcon = { volume_discount: Layers, tiered_pricing: Tag, bundle: Zap };
  const typeLabel = { volume_discount: 'Volume Discount', tiered_pricing: 'Tiered Pricing', bundle: 'Bundle' };
  const typeColor = { volume_discount: 'text-blue-500', tiered_pricing: 'text-amber-500', bundle: 'text-purple-500' };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="cpq-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">CPQ Pricing Rules</h1>
          <p className="text-sm text-muted-foreground mt-1">Volume discounts, tiered pricing, and bundle configurations</p>
        </div>
        <Button onClick={() => { setShowForm(true); setEditingRule(null); }} data-testid="add-pricing-rule-btn">
          <Plus className="h-4 w-4 mr-1" />Add Pricing Rule
        </Button>
      </div>

      {showForm && (
        <PricingRuleForm
          rule={editingRule}
          onClose={() => { setShowForm(false); setEditingRule(null); }}
          onSave={() => { setShowForm(false); setEditingRule(null); load(); }}
        />
      )}

      {rules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calculator className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No pricing rules configured.</p>
            <p className="text-xs text-muted-foreground/70 mt-1">Add volume discounts, tiered pricing, or bundles to optimize your quotes.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rules.map(r => {
            const Icon = typeIcon[r.rule_type] || Calculator;
            const color = typeColor[r.rule_type] || 'text-primary';
            return (
              <Card key={r.id} data-testid={`pricing-rule-${r.id}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-primary/10"><Icon className={`h-4 w-4 ${color}`} /></div>
                      <div>
                        <h3 className="font-semibold text-sm">{r.name}</h3>
                        <Badge variant="outline" className="text-[10px] mt-0.5">{typeLabel[r.rule_type] || r.rule_type}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingRule(r); setShowForm(true); }}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(r.id)} data-testid={`delete-rule-${r.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Tiers display */}
                  {r.tiers?.length > 0 && (
                    <div className="space-y-1">
                      {r.tiers.map((t, i) => (
                        <div key={i} className="flex items-center justify-between text-xs p-1.5 rounded bg-muted/50">
                          <span>{t.min_qty}{t.max_qty ? `-${t.max_qty}` : '+'} units</span>
                          <span className="font-medium">
                            {t.unit_price_override != null ? `$${t.unit_price_override}` : `${t.discount_pct}% off`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {r.rule_type === 'bundle' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Bundle discount: {r.bundle_discount_pct}% &middot; {r.bundle_item_ids?.length || 0} items
                    </p>
                  )}

                  <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                    <span>Max cap: {r.max_discount_cap}%</span>
                    <Badge variant={r.is_active ? 'default' : 'secondary'} className="text-[9px]">{r.is_active ? 'Active' : 'Inactive'}</Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Pricing Rule Form ─── */
function PricingRuleForm({ rule, onClose, onSave }) {
  const [form, setForm] = useState({
    name: rule?.name || '',
    rule_type: rule?.rule_type || 'volume_discount',
    max_discount_cap: rule?.max_discount_cap || 50,
    bundle_discount_pct: rule?.bundle_discount_pct || 0,
    tiers: rule?.tiers || [{ min_qty: 1, max_qty: 10, discount_pct: 5 }],
  });
  const [saving, setSaving] = useState(false);

  const addTier = () => setForm({ ...form, tiers: [...form.tiers, { min_qty: 1, max_qty: null, discount_pct: 0 }] });
  const removeTier = (i) => setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) });
  const updateTier = (i, field, value) => {
    const updated = [...form.tiers];
    updated[i] = { ...updated[i], [field]: value };
    setForm({ ...form, tiers: updated });
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Name is required');
    setSaving(true);
    try {
      const body = {
        ...form,
        tiers: form.tiers.map(t => ({
          min_qty: parseInt(t.min_qty) || 1,
          max_qty: t.max_qty ? parseInt(t.max_qty) : null,
          discount_pct: parseFloat(t.discount_pct) || 0,
          unit_price_override: t.unit_price_override ? parseFloat(t.unit_price_override) : null,
        })),
      };
      if (rule?.id) {
        await cpqAPI.updateRule(rule.id, body);
        toast.success('Rule updated');
      } else {
        await cpqAPI.createRule(body);
        toast.success('Rule created');
      }
      onSave();
    } catch (e) { toast.error('Failed to save rule'); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="pricing-rule-form">
      <CardHeader><CardTitle className="text-sm">{rule ? 'Edit' : 'Create'} Pricing Rule</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input placeholder="Rule name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="rule-name" />
          <select className="border rounded-md px-3 py-2 text-sm bg-background" value={form.rule_type}
            onChange={e => setForm({ ...form, rule_type: e.target.value })} data-testid="rule-type-select">
            <option value="volume_discount">Volume Discount</option>
            <option value="tiered_pricing">Tiered Pricing</option>
            <option value="bundle">Bundle</option>
          </select>
          <Input type="number" placeholder="Max discount cap %" value={form.max_discount_cap}
            onChange={e => setForm({ ...form, max_discount_cap: parseFloat(e.target.value) || 0 })} data-testid="max-cap" />
        </div>

        {form.rule_type !== 'bundle' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium">Pricing Tiers</p>
              <Button variant="outline" size="sm" onClick={addTier} data-testid="add-tier-btn"><Plus className="h-3 w-3 mr-1" />Add Tier</Button>
            </div>
            <div className="space-y-2">
              {form.tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`tier-row-${i}`}>
                  <Input type="number" placeholder="Min qty" value={t.min_qty} className="w-24"
                    onChange={e => updateTier(i, 'min_qty', e.target.value)} />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input type="number" placeholder="Max (empty=unlimited)" value={t.max_qty || ''} className="w-24"
                    onChange={e => updateTier(i, 'max_qty', e.target.value)} />
                  <span className="text-xs text-muted-foreground">units:</span>
                  <Input type="number" placeholder="Discount %" value={t.discount_pct} className="w-24"
                    onChange={e => updateTier(i, 'discount_pct', e.target.value)} />
                  <span className="text-xs text-muted-foreground">% off</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeTier(i)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {form.rule_type === 'bundle' && (
          <Input type="number" placeholder="Bundle discount %" value={form.bundle_discount_pct}
            onChange={e => setForm({ ...form, bundle_discount_pct: parseFloat(e.target.value) || 0 })} data-testid="bundle-discount" />
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-pricing-rule">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            {rule ? 'Update' : 'Create'} Rule
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
